import { ACCOUNTS_API_URL } from '../account/api';
import { getDeviceId } from '../account/session';
import { languageForPath } from '../util/language-for-path';
import type { StageRunOutcome } from '../pipeline/runner';
import type { OptimizationSettings } from '../pipeline/settings';

/**
 * Shape version of the opaque `settings`/`stageReport` JSON blobs — embedded *inside* each blob, not
 * sent as its own field. Bump only when the shape of those blobs meaningfully changes (a stage
 * removed, a param restructured) so analysis tooling can branch on historical rows. Distinct from
 * `naulonVersion`, which moves every release regardless of blob shape.
 */
export const TELEMETRY_SCHEMA_VERSION = 1;

/** Tier 2 — actual user content, only sent when Benchmark comparison mode was the opt-in action. */
export interface TelemetryTier2Input {
  prompt: string;
  promptProcessed: string;
  /** Structured, not pre-stringified — the server caps individual blocks before serializing, so a
   * truncated submission still comes out as valid JSON rather than a cut-off string. */
  context: { label: string; content: string }[];
  output: string;
  tokenOrigCost?: number;
}

/**
 * Mirrors the `POST /telemetry` body, minus `deviceId` — that's filled in here so no call site has
 * to thread it. Everything except `tier2` is Tier 1: metadata only, no user content.
 */
export interface TelemetryInput {
  model?: string;
  naulonVersion?: string;
  settings?: OptimizationSettings;
  stageReport?: StageRunOutcome[];
  elapsedMs?: number;
  timedOut?: boolean;
  tokensBefore?: number;
  tokensAfter?: number;
  tokenCost?: number;
  tokenEstSaved?: number;
  userAgent?: string;
  languageMix?: Record<string, number>;
  agentMode?: boolean;
  agentPersona?: string;
  tier2?: TelemetryTier2Input;
}

/**
 * Counts the languages of the files currently in context. Only the three the pipeline actually
 * recognizes are bucketed (see languageForPath); anything else is simply not counted, which is the
 * point — it answers "how much does Python coverage matter in real usage" without naming any file.
 */
export function computeLanguageMix(paths: string[]): Record<string, number> {
  const mix: Record<string, number> = {};
  for (const path of paths) {
    const language = languageForPath(path);
    if (language) mix[language] = (mix[language] ?? 0) + 1;
  }
  return mix;
}

/**
 * Fire-and-forget telemetry push. Deliberately not async from the caller's perspective: the fetch is
 * never awaited and every failure — unconfigured backend, offline, 400, 429, a serialization throw —
 * is swallowed. Telemetry is metadata about the app, not a feature of it, so it must never surface
 * an error or delay a send. There is no retry or offline queue by design.
 */
export function pushTelemetry(input: TelemetryInput): void {
  // Empty when VITE_ACCOUNTS_API_URL wasn't set at build time — local-only/no-backend is a
  // first-class mode here, so no-op silently rather than logging on every request.
  if (!ACCOUNTS_API_URL) return;

  try {
    const body = {
      deviceId: getDeviceId(),
      model: input.model,
      naulonVersion: input.naulonVersion,
      settings: input.settings ? { schemaVersion: TELEMETRY_SCHEMA_VERSION, ...input.settings } : undefined,
      stageReport: input.stageReport ? { schemaVersion: TELEMETRY_SCHEMA_VERSION, stages: input.stageReport } : undefined,
      elapsedMs: input.elapsedMs,
      timedOut: input.timedOut,
      tokensBefore: input.tokensBefore,
      tokensAfter: input.tokensAfter,
      tokenCost: input.tokenCost,
      tokenEstSaved: input.tokenEstSaved,
      userAgent: input.userAgent,
      languageMix: input.languageMix,
      agentMode: input.agentMode,
      agentPersona: input.agentPersona,
      tier2: input.tier2,
    };

    // Plain fetch, not api.ts's request() helper: that one throws on non-2xx and is built around
    // session auth, neither of which applies to an anonymous best-effort write.
    void fetch(`${ACCOUNTS_API_URL}/telemetry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).catch(() => {});
  } catch {
    // Unreachable in practice (JSON.stringify on plain data), but a telemetry push must not be able
    // to take a send down with it.
  }
}
